import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../../App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Cake, Gift, Bell, ChevronDown, ChevronUp, Send, X, Sparkles, PartyPopper, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export default function BirthdayReminderWidget({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchBirthdays = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/children/birthdays/tomorrow");
      setData(res.data);

      if (res.data.total_tomorrow > 0) {
        const sessionKey = `birthday_reminder_shown_${res.data.tomorrow_date}`;
        if (!sessionStorage.getItem(sessionKey)) {
          setShowPopup(true);
          sessionStorage.setItem(sessionKey, "true");
        }
      }
    } catch (err) {
      console.error("Error fetching birthday reminders:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBirthdays();
  }, [fetchBirthdays]);

  const dismissPopup = () => setShowPopup(false);

  const handleNotify = async () => {
    setSending(true);
    try {
      const res = await axiosInstance.post("/children/birthdays/notify-tomorrow");
      if (res.data.success) {
        toast.success(res.data.message);
      }
    } catch (err) {
      toast.error("Gagal menghantar notifikasi");
    } finally {
      setSending(false);
    }
  };

  if (loading || !data) return null;
  if (data.total_tomorrow === 0 && data.total_today === 0) return null;

  const { tomorrow_birthdays, today_birthdays, total_tomorrow, total_today } = data;
  const hasUpcoming = total_tomorrow > 0;
  const hasToday = total_today > 0;

  return (
    <>
      <div
        data-testid="birthday-reminder-widget"
        className={`mb-6 rounded-2xl shadow-lg border-2 overflow-hidden transition-all duration-300 ${
          hasUpcoming
            ? "bg-gradient-to-r from-pink-50 via-purple-50 to-indigo-50 border-pink-300"
            : "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300"
        }`}
      >
        <div
          className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 px-4 py-3 flex items-center justify-between cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm relative">
              <Cake className="w-6 h-6 text-white" />
              {hasUpcoming && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center text-xs font-black text-purple-900 shadow-md">
                  {total_tomorrow}
                </span>
              )}
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">
                {hasUpcoming ? "Hari Jadi Esok!" : "Hari Jadi Hari Ini!"}
              </h3>
              <p className="text-white/80 text-xs">
                {hasUpcoming
                  ? `${total_tomorrow} kanak-kanak berhari jadi esok`
                  : `${total_today} kanak-kanak berhari jadi hari ini`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-lg backdrop-blur-sm">
              <Gift className="w-3 h-3 text-white" />
              <span className="text-white text-xs font-bold">Peringatan</span>
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-white" />
            ) : (
              <ChevronDown className="w-5 h-5 text-white" />
            )}
          </div>
        </div>

        {expanded && (
          <div className="px-4 py-4 space-y-4">
            {hasUpcoming && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="w-4 h-4 text-pink-500" />
                  <p className="text-xs font-bold text-pink-600 uppercase tracking-wide">
                    Hari Jadi Esok
                  </p>
                </div>
                <div className="space-y-2">
                  {tomorrow_birthdays.map((child) => (
                    <BirthdayChildCard key={child.id} child={child} variant="tomorrow" />
                  ))}
                </div>
              </div>
            )}

            {hasToday && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <PartyPopper className="w-4 h-4 text-amber-500" />
                  <p className="text-xs font-bold text-amber-600 uppercase tracking-wide">
                    Hari Jadi Hari Ini
                  </p>
                </div>
                <div className="space-y-2">
                  {today_birthdays.map((child) => (
                    <BirthdayChildCard key={child.id} child={child} variant="today" />
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              {hasUpcoming && (user.role === "guru" || user.role === "admin") && (
                <Button
                  data-testid="notify-birthday-btn"
                  onClick={handleNotify}
                  disabled={sending}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl font-bold text-sm shadow-lg"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? "Menghantar..." : "Hantar Peringatan Hari Jadi"}
                </Button>
              )}
              <Button
                data-testid="view-children-btn"
                onClick={() => navigate("/children")}
                variant="outline"
                className="flex-1 border-2 border-purple-300 text-purple-700 hover:bg-purple-50 rounded-xl font-bold text-sm"
              >
                <Cake className="w-4 h-4 mr-2" />
                Lihat Senarai Kanak-kanak
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showPopup} onOpenChange={(open) => { if (!open) dismissPopup(); }}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl" data-testid="birthday-popup-modal">
          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 p-5 text-center relative overflow-hidden">
            <button
              onClick={dismissPopup}
              className="absolute top-3 right-3 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors z-10"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            <Sparkles className="absolute top-4 left-4 w-5 h-5 text-yellow-300 animate-pulse" />
            <Star className="absolute bottom-4 left-8 w-4 h-4 text-pink-300 animate-pulse" style={{ animationDelay: "0.5s" }} />
            <Star className="absolute top-8 right-12 w-3 h-3 text-yellow-300 animate-pulse" style={{ animationDelay: "1s" }} />
            
            <div className="w-16 h-16 mx-auto bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm mb-3">
              <Cake className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-white font-black text-lg">Peringatan Hari Jadi</h2>
            <p className="text-white/80 text-sm mt-1">
              {total_tomorrow} kanak-kanak berhari jadi esok!
            </p>
          </div>

          <div className="p-5 space-y-4">
            <div className="max-h-56 overflow-y-auto space-y-2">
              {tomorrow_birthdays.map((child) => (
                <div
                  key={child.id}
                  className="flex items-center gap-3 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl px-4 py-3 border border-pink-200"
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-lg font-black shadow-md">
                      {child.name?.charAt(0) || "?"}
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-yellow-400 flex items-center justify-center shadow border-2 border-white">
                      <span className="text-xs">🎂</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{child.name}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-pink-200 text-pink-700 px-2 py-0.5 rounded-full font-bold">
                        {child.age} Tahun
                      </span>
                      {child.class_name && (
                        <span className="text-xs text-gray-500 truncate">
                          {child.class_name?.split("(")[0]?.trim()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-2">
              {(user.role === "guru" || user.role === "admin") && (
                <Button
                  data-testid="popup-notify-birthday-btn"
                  onClick={() => { handleNotify(); dismissPopup(); }}
                  disabled={sending}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold rounded-xl py-3 shadow-lg"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Hantar Peringatan Kepada Penyelia
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={dismissPopup}
                className="w-full text-gray-500 hover:text-gray-700 font-medium text-sm"
              >
                Tutup
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BirthdayChildCard({ child, variant }) {
  const isTomorrow = variant === "tomorrow";
  return (
    <div className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border shadow-sm ${
      isTomorrow
        ? "bg-white border-pink-200"
        : "bg-white border-amber-200"
    }`}>
      <div className="relative flex-shrink-0">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-black shadow ${
          isTomorrow
            ? "bg-gradient-to-br from-pink-400 to-purple-500"
            : "bg-gradient-to-br from-amber-400 to-orange-500"
        }`}>
          {child.name?.charAt(0) || "?"}
        </div>
        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-yellow-400 flex items-center justify-center shadow border-2 border-white">
          <span className="text-[10px]">🎂</span>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-gray-800 truncate">{child.name}</p>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
            isTomorrow ? "bg-pink-100 text-pink-600" : "bg-amber-100 text-amber-600"
          }`}>
            {child.age} Tahun
          </span>
          <span className="text-xs text-gray-500 truncate">
            {child.class_name?.split("(")[0]?.trim()}
          </span>
        </div>
      </div>
      {isTomorrow && (
        <div className="flex-shrink-0">
          <Gift className="w-4 h-4 text-pink-400" />
        </div>
      )}
    </div>
  );
}
